import React, { useState, useContext, useEffect, useRef } from 'react';
import { Table } from "reactstrap";
import { AppContext } from '../application/provider';
import PersonalReclutamientoService from "../axios_services/personalreclutamiento.service";
import Loader from "../components/Loader/Loader";
import { ButtonSearch, ButtonInsert, ButtonDelete, ButtonValidar, ButtonPersona } from '../components/Buttons/Buttons';
import ModalPersonalReclutamiento from '../components/Modal/ModalPersonalReclutamiento';
import ModalValidacionReclutamiento from '../components/Modal/ModalValidacionReclutamiento';
import ModalCandidatoReclutamiento from '../components/Modal/ModalCandidatoReclutamiento';

const PERFILES_VER_TODOS = [8, 26];
const AREA_TH = 2;

const ESTADOS = {
    0: { label: 'Pendiente', color: '#ff9741', bg: '#fff3e0' },
    1: { label: 'Aprobado Jefe', color: '#1565c0', bg: '#e3f2fd' },
    2: { label: 'Aprobado TH', color: '#2e7d32', bg: '#e8f5e9' },
    3: { label: 'Observado', color: '#b71c1c', bg: '#ffebee' },
};

const TIPOS = {
    1: { label: 'RECLUTAMIENTO' },
    2: { label: 'INV. DIRECTA' },
};

function BadgeEstado({ estado }) {
    const info = ESTADOS[estado] || { label: `Estado ${estado}`, color: '#666', bg: '#eee' };
    return (
        <span style={{
            background: info.bg,
            color: info.color,
            border: `1px solid ${info.color}`,
            borderRadius: '20px',
            padding: '2px 10px',
            fontSize: '9px',
            fontWeight: 700,
            whiteSpace: 'nowrap',
            display: 'inline-block',
        }}>
            {info.label}
        </span>
    );
}

function BadgeTipo({ tipo }) {
    const info = TIPOS[tipo] || { label: `Tipo ${tipo}` };
    return (
        <span style={{
            background: tipo === 2 ? '#f3e5f5' : '#e8eaf6',
            color: tipo === 2 ? '#6a1b9a' : '#283593',
            border: `1px solid ${tipo === 2 ? '#ce93d8' : '#9fa8da'}`,
            borderRadius: '20px',
            padding: '2px 10px',
            fontSize: '9px',
            fontWeight: 700,
            whiteSpace: 'nowrap',
        }}>
            {info.label}
        </span>
    );
}

function PersonalReclutamiento() {
    const [state] = useContext(AppContext);
    const [registros, setRegistros] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [tipoFiltro, setTipoFiltro] = useState('');

    const [modalNuevo, setModalNuevo] = useState(false);
    const [modalValidacion, setModalValidacion] = useState({ open: false, registro: null, modo: null });
    const [modalCandidato, setModalCandidato] = useState({ open: false, registro: null });

    const initialFetchDone = useRef(false);

    const [periodo, setPeriodo] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });

    const [periodoActivo, setPeriodoActivo] = useState(periodo);

    const currentUser = state?.user;
    const currentUserId = currentUser?.usuario_id ? parseInt(currentUser.usuario_id) : null;
    const currentPerfilId = currentUser?.perfil_id ? parseInt(currentUser.perfil_id) : null;
    const currentAreaId = currentUser?.area_id ? parseInt(currentUser.area_id) : null;

    const puedeVerTodos = PERFILES_VER_TODOS.includes(currentPerfilId) || currentAreaId === AREA_TH;

    const loadData = async () => {
        setIsLoading(true);
        try {
            const response = await PersonalReclutamientoService.getReclutamiento(periodo);
            setRegistros(response.data || []);
            setPeriodoActivo(periodo);
        } catch (error) {
            console.error(error);
            setRegistros([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!initialFetchDone.current) {
            loadData();
            initialFetchDone.current = true;
        }
    }, []);

    const puedeAprobarJefe = (reg) => {
        if (parseInt(reg.estado_solicitud) !== 0) return false;
        if (parseInt(reg.tipo_solicitud) !== 1) return false;
        return parseInt(reg.jefe_usuario_id) === currentUserId;
    };

    const puedeAprobarTH = (reg) => {
        if (!puedeVerTodos) return false;
        const est = parseInt(reg.estado_solicitud);
        const tipo = parseInt(reg.tipo_solicitud);
        if (tipo === 1) return est === 1;
        if (tipo === 2) return est === 0;
        return false;
    };

    const puedeObservar = (reg) => {
        const est = parseInt(reg.estado_solicitud);
        const tipo = parseInt(reg.tipo_solicitud);
        if (est === 2 || est === 3) return false;
        if (parseInt(reg.jefe_usuario_id) === currentUserId && tipo === 1 && est === 0) return true;
        if (puedeVerTodos && (est === 0 || est === 1)) return true;
        return false;
    };

    const puedeEditarCandidato = (reg) => {
        const est = parseInt(reg.estado_solicitud);
        const tipo = parseInt(reg.tipo_solicitud);
        const esPropietario = parseInt(reg.solicitante_id) === currentUserId;
        if (tipo === 1) return est === 2 && (esPropietario || puedeVerTodos);
        if (tipo === 2) return esPropietario || puedeVerTodos;
        return false;
    };

    const puedeEliminar = (reg) =>
        parseInt(reg.solicitante_id) === currentUserId && parseInt(reg.estado_solicitud) === 0;

    const handleNuevo = async (data) => {
        await PersonalReclutamientoService.insertReclutamiento(data);
    };

    const handleAprobarJefe = async (comentario) => {
        await PersonalReclutamientoService.aprobarJefe({
            reclutamiento_id: modalValidacion.registro.reclutamiento_id,
        });
    };

    const handleAprobarTH = async (comentario) => {
        await PersonalReclutamientoService.aprobarTH({
            reclutamiento_id: modalValidacion.registro.reclutamiento_id,
            comentario: comentario || null,
        });
    };

    const handleObservar = async (comentario) => {
        await PersonalReclutamientoService.observar({
            reclutamiento_id: modalValidacion.registro.reclutamiento_id,
            comentario,
        });
    };

    const handleEliminar = async (registro) => {
        if (!window.confirm('¿Está seguro de eliminar esta solicitud?')) return;
        setIsLoading(true);
        try {
            await PersonalReclutamientoService.deleteReclutamiento({
                reclutamiento_id: registro.reclutamiento_id,
            });
            await loadData();
        } catch (error) {
            alert(error.response?.data?.message || 'Error al eliminar.');
        } finally {
            setIsLoading(false);
        }
    };

    const abrirValidacion = (registro) => {
        let modo = null;
        if (puedeAprobarJefe(registro)) modo = 'jefe';
        else if (puedeAprobarTH(registro)) modo = 'th';
        else if (puedeObservar(registro)) {
            if (parseInt(registro.jefe_usuario_id) === currentUserId) modo = 'jefe';
            else if (puedeVerTodos) modo = 'th';
        }
        setModalValidacion({ open: true, registro, modo });
    };

    const tituloValidacion = () => {
        const modo = modalValidacion.modo;
        if (modo === 'jefe') return 'VALIDACION DE JEFE INMEDIATO';
        if (modo === 'th') return 'VALIDACION DE TALENTO HUMANO';
        return 'VALIDAR SOLICITUD';
    };

    const onAprobarModal = async (comentario) => {
        if (modalValidacion.modo === 'jefe') await handleAprobarJefe(comentario);
        else await handleAprobarTH(comentario);
    };

    const formatFecha = (fecha) => {
        if (!fecha) return '-';
        return fecha.split('T')[0];
    };

    if (isLoading) return <Loader />;

    const now = new Date();
    const currentMonthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const isCurrentMonth = periodoActivo === currentMonthStr;

    const registrosFiltrados = registros.filter(reg => {
        if (tipoFiltro === '') return true;
        return reg.tipo_solicitud.toString() === tipoFiltro;
    });

    return (
        <div className="caja-flujo-container">
            <h1>RECLUTAMIENTO DE PERSONAL</h1>

            <div className="filtros">
                <div className="campo-fecha">
                    <label>Periodo:</label>
                    <input
                        type="month"
                        value={periodo}
                        onChange={(e) => setPeriodo(e.target.value)}
                    />
                </div>
                <div className="campo-fecha">
                    <label>Tipo:</label>
                    <select
                        value={tipoFiltro}
                        onChange={(e) => setTipoFiltro(e.target.value)}
                        className="form-control"
                    >
                        <option value="">Todos</option>
                        <option value="1">Reclutamiento</option>
                        <option value="2">Inv. Directa</option>
                    </select>
                </div>
            </div>

            <ButtonSearch onClick={loadData} isLoading={isLoading} />

            {isCurrentMonth && (
                <div className="nuevo-movimiento-section">
                    <ButtonInsert onClick={() => setModalNuevo(true)} />
                </div>
            )}

            <div className="table-container">
                <Table>
                    <thead>
                        <tr>
                            <th>N°</th>
                            <th>Tipo</th>
                            <th>Solicitante</th>
                            <th>Jefe Inmediato</th>
                            <th>Solicitud</th>
                            <th>Fecha Envio</th>
                            <th>Estado</th>
                            <th>Comentario</th>
                            <th>Fecha Inicio</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {registrosFiltrados.length > 0 ? (
                            registrosFiltrados.map((reg, index) => {
                                const numeroOrden = registrosFiltrados.length - index;
                                const mostrarBtnValidar = puedeAprobarJefe(reg) || puedeAprobarTH(reg) || puedeObservar(reg);
                                const mostrarBtnCandidato = puedeEditarCandidato(reg);
                                const mostrarBtnEliminar = puedeEliminar(reg);

                                return (
                                    <tr key={reg.reclutamiento_id}>
                                        <td>{numeroOrden}</td>
                                        <td><BadgeTipo tipo={parseInt(reg.tipo_solicitud)} /></td>
                                        <td style={{ whiteSpace: 'nowrap' }}>
                                            {(reg.nombre_solicitante || reg.solicitante_id?.toString() || '').toUpperCase()}
                                        </td>
                                        <td style={{ whiteSpace: 'nowrap' }}>
                                            {(reg.nombre_jefe || '-').toUpperCase()}
                                        </td>
                                        <td style={{ maxWidth: '220px', whiteSpace: 'pre-wrap' }}>
                                            {(reg.solicitud || '-').toUpperCase()}
                                        </td>
                                        <td style={{ whiteSpace: 'nowrap' }}>{formatFecha(reg.fecha_envio)}</td>
                                        <td><BadgeEstado estado={parseInt(reg.estado_solicitud)} /></td>
                                        <td style={{ maxWidth: '150px', color: '#b71c1c', fontSize: '9px' }}>
                                            {(reg.comentario || '-').toUpperCase()}
                                        </td>
                                        <td style={{ whiteSpace: 'nowrap' }}>{formatFecha(reg.fecha_inicio)}</td>
                                        <td>
                                            <div className="acciones-container">
                                                {mostrarBtnValidar && (
                                                    <ButtonValidar
                                                        onClick={() => abrirValidacion(reg)}
                                                        title={
                                                            puedeAprobarTH(reg) ? 'Aprobar como TH' :
                                                                puedeAprobarJefe(reg) ? 'Aprobar como Jefe' : 'Validar'
                                                        }
                                                    />
                                                )}
                                                {mostrarBtnCandidato && (
                                                    <ButtonPersona
                                                        onClick={() => setModalCandidato({ open: true, registro: reg })}
                                                        title="Candidatos"
                                                    />
                                                )}
                                                {mostrarBtnEliminar && (
                                                    <ButtonDelete onClick={() => handleEliminar(reg)} />
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={10} className="sin-data">
                                    No hay solicitudes de reclutamiento para este periodo.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            <ModalPersonalReclutamiento
                isOpen={modalNuevo}
                onSave={handleNuevo}
                onClose={(reload) => {
                    setModalNuevo(false);
                    if (reload === true) loadData();
                }}
                onModalClosed={() => { }}
            />

            <ModalValidacionReclutamiento
                isOpen={modalValidacion.open}
                registro={modalValidacion.registro}
                titulo={tituloValidacion()}
                onAprobar={onAprobarModal}
                onObservar={handleObservar}
                onClose={(reload) => {
                    setModalValidacion({ open: false, registro: null, modo: null });
                    if (reload === true) loadData();
                }}
                onModalClosed={() => { }}
            />

            <ModalCandidatoReclutamiento
                isOpen={modalCandidato.open}
                registro={modalCandidato.registro}
                onClose={() => setModalCandidato({ open: false, registro: null })}
                onModalClosed={() => { }}
            />
        </div>
    );
}

export default PersonalReclutamiento;